import {Component, Inject, OnInit} from "@angular/core";
import {Resource} from "../../../models/resource";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {locale as polish} from './../i18n/pl';
import {FuseTranslationLoaderService} from "../../../../../@fuse/services/translation-loader.service";
import {ResourceService} from "../../../services/resource.service";
import {first} from "rxjs/operators";

@Component({
    selector: 'device-stats-modal-component',
    templateUrl: './deviceStatsModal.component.html',
    styleUrls: ['./deviceStatsModal.component.scss']
})
export class DeviceStatsModalComponent implements OnInit {
    public resource: Resource;
    public loading: boolean = true;
    public error: string | null = null;
    public chartData: any[] = [];
    public chartLabels: string[] = [];
    public totalTrainings: number = 0;
    public selectedPeriod: number = 30; // days
    public startDate: string = '';
    public endDate: string = '';

    // Chart configuration
    public chartOptions: any = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            yAxes: [{
                ticks: {
                    beginAtZero: true,
                    stepSize: 1
                }
            }]
        },
        legend: {
            display: true,
            position: 'bottom'
        },
        tooltips: {
            enabled: true,
            mode: 'index',
            intersect: false
        }
    };

    public chartColors: any[] = [
        {
            backgroundColor: 'rgba(63, 81, 181, 0.2)',
            borderColor: 'rgba(63, 81, 181, 1)',
            pointBackgroundColor: 'rgba(63, 81, 181, 1)',
            pointBorderColor: '#fff',
            pointHoverBackgroundColor: '#fff',
            pointHoverBorderColor: 'rgba(63, 81, 181, 0.8)'
        }
    ];

    public chartType: string = 'line';

    constructor(
        public dialogRef: MatDialogRef<DeviceStatsModalComponent>,
        @Inject(MAT_DIALOG_DATA) private _data: any,
        private translationLoader: FuseTranslationLoaderService,
        private resourceService: ResourceService
    ) {
        this.resource = _data.resource;
        this.translationLoader.loadTranslations(polish);
    }

    ngOnInit(): void {
        this.calculateDates();
        this.loadStatistics();
    }

    calculateDates(): void {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - this.selectedPeriod);

        this.endDate = this.formatDate(end);
        this.startDate = this.formatDate(start);
    }

    formatDate(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    onPeriodChange(): void {
        this.calculateDates();
        this.loadStatistics();
    }

    loadStatistics(): void {
        this.loading = true;
        this.error = null;

        this.resourceService.getDeviceStatsDaily(this.resource.id, this.startDate, this.endDate)
            .pipe(first())
            .subscribe(
                (response) => {
                    this.totalTrainings = response.total || 0;
                    this.processChartData(response.items || []);
                    this.loading = false;
                },
                (error) => {
                    console.error('Error loading device statistics:', error);
                    this.error = 'Failed to load statistics';
                    this.loading = false;
                }
            );
    }

    processChartData(items: any[]): void {
        // Fill in missing dates with zero values
        const dataMap = new Map<string, number>();
        items.forEach(item => {
            dataMap.set(item.date, item.count);
        });

        // Generate all dates in range
        const labels: string[] = [];
        const data: number[] = [];
        const start = new Date(this.startDate);
        const end = new Date(this.endDate);

        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            const dateStr = this.formatDate(new Date(d));
            labels.push(this.formatDisplayDate(dateStr));
            data.push(dataMap.get(dateStr) || 0);
        }

        this.chartLabels = labels;
        this.chartData = [
            {
                data: data,
                label: 'Number of trainings'
            }
        ];
    }

    formatDisplayDate(dateStr: string): string {
        const [year, month, day] = dateStr.split('-');
        return `${day}.${month}`;
    }

    onNoClick(): void {
        this.dialogRef.close();
    }
}